import { Injectable } from '@angular/core';

/**
 * Központosított formázási szolgáltatás
 * Összes formázási funkcionalitás egy helyen
 */
@Injectable({
  providedIn: 'root'
})
export class FormattingUtilsService {

  /**
   * Format currency for display
   */
  formatCurrency(amount: number, currency: string = 'HUF'): string {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Format large numbers with Hungarian thousand separators
   */
  formatNumber(number: number): string {
    return new Intl.NumberFormat('hu-HU').format(number);
  }

  /**
   * Format percentage values
   */
  formatPercentage(value: number, decimals: number = 1): string {
    return `${value.toFixed(decimals)}%`;
  }

  /**
   * Get status color for UI display
   */
  getStatusColor(status: string): string {
    switch (status) {
      case 'active':
        return '#4CAF50';
      case 'inactive':
        return '#F44336';
      case 'pending':
        return '#FF9800';
      case 'suspended':
        return '#9E9E9E';
      case 'hivando':
        return '#2196F3';
      case 'callback':
        return '#FF9800';
      case 'rejected':
        return '#F44336';
      case 'robinson':
        return '#9E9E9E';
      case 'no_answer':
        return '#607D8B';
      case 'appointment':
        return '#4CAF50';
      default:
        return '#2196F3';
    }
  }

  /**
   * Get status display text in Hungarian
   */
  getStatusText(status: string): string {
    switch (status) {
      case 'active':
        return 'Aktív';
      case 'inactive':
        return 'Inaktív';
      case 'pending':
        return 'Függőben';
      case 'suspended':
        return 'Felfüggesztett';
      case 'hivando':
        return 'Hívandó';
      case 'callback':
        return 'Visszahívás';
      case 'rejected':
        return 'Elutasított';
      case 'robinson':
        return 'Robinson lista';
      case 'no_answer':
        return 'Nem veszi fel';
      case 'appointment':
        return 'Időpont egyeztetett';
      default:
        return status;
    }
  }

  /**
   * Format phone number for display
   */
  formatPhoneNumber(phone: string): string {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Hungarian mobile numbers (20, 30, 70)
    if (cleaned.match(/^36(20|30|70)\d{7}$/)) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 11)}`;
    }
    
    // Hungarian landline numbers
    if (cleaned.match(/^36\d{8,9}$/)) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
    }
    
    // Other international numbers
    if (cleaned.length >= 10) {
      return `+${cleaned}`;
    }
    
    return phone; // Return original if doesn't match any pattern
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Truncate text with ellipsis
   */
  truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  }

  /**
   * Capitalize first letter of each word
   */
  titleCase(text: string): string {
    return text.replace(/\w\S*/g, (txt) => 
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }

  /**
   * Convert Hungarian special characters for search/comparison
   */
  normalizeHungarianText(text: string): string {
    const accents: { [key: string]: string } = {
      'á': 'a', 'à': 'a', 'ä': 'a', 'â': 'a',
      'é': 'e', 'è': 'e', 'ë': 'e', 'ê': 'e',
      'í': 'i', 'ì': 'i', 'ï': 'i', 'î': 'i',
      'ó': 'o', 'ò': 'o', 'ö': 'o', 'ô': 'o', 'ő': 'o',
      'ú': 'u', 'ù': 'u', 'ü': 'u', 'û': 'u', 'ű': 'u'
    };
    
    return text.toLowerCase().replace(/[áàäâéèëêíìïîóòöôőúùüûű]/g, (match) => accents[match]);
  }

  /**
   * Format Hungarian tax number
   */
  formatTaxNumber(taxNumber: string): string {
    if (!taxNumber) return '';
    
    const cleaned = taxNumber.replace(/\D/g, '');
    
    // Hungarian tax number format: 12345678-1-23
    if (cleaned.length === 11) {
      return `${cleaned.slice(0, 8)}-${cleaned.slice(8, 9)}-${cleaned.slice(9, 11)}`;
    }
    
    return taxNumber;
  }

  /**
   * Format Hungarian company registration number
   */
  formatCompanyRegistrationNumber(regNumber: string): string {
    if (!regNumber) return '';
    
    const cleaned = regNumber.replace(/\D/g, '');
    
    // Hungarian company registration format: 01-09-123456
    if (cleaned.length >= 8) {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4)}`;
    }
    
    return regNumber;
  }
}